import type { User } from '@plane/shared';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { useAuth } from '@/modules/auth/hooks/auth-context';
import { useUpdateUser, useUsers } from '@/modules/users/hooks/use-users';
import { ApiRequestError } from '@/services/http-client';

const POR_PAGINA = 20;

function mensagemDeErro(erro: unknown): string {
  return erro instanceof ApiRequestError
    ? erro.message
    : 'Não foi possível falar com o servidor. Verifique a conexão e tente de novo.';
}

const PAPEL: Record<User['role'], string> = {
  ADMIN: 'Administrador',
  EDITOR: 'Editor',
};

function Etiqueta({ ativo }: { ativo: boolean }) {
  return (
    <span
      className={`inline-block px-2 py-0.5 text-xs ${
        ativo ? 'bg-brand-150 text-brand-950' : 'bg-sand text-ink-soft'
      }`}
    >
      {ativo ? 'Ativo' : 'Inativo'}
    </span>
  );
}

export function UsersListPage() {
  const { usuario } = useAuth();
  const [pagina, setPagina] = useState(1);
  const [erroDeAcao, setErroDeAcao] = useState<string | null>(null);

  const consulta = useUsers(pagina, POR_PAGINA);
  const atualizar = useUpdateUser();

  const ocupado = atualizar.isPending;

  async function alternarAtivo(alvo: User) {
    setErroDeAcao(null);

    try {
      await atualizar.mutateAsync({ id: alvo.id, input: { isActive: !alvo.isActive } });
    } catch (falha) {
      setErroDeAcao(mensagemDeErro(falha));
    }
  }

  const cabecalho = (
    <div className="flex items-start justify-between gap-4">
      <div>
        <h1 className="font-display text-2xl">Usuários</h1>
        <p className="mt-1 text-sm text-ink-soft">Contas com acesso ao painel.</p>
      </div>

      <Link
        to="/usuarios/novo"
        className="bg-brand-600 px-4 py-2.5 text-sm text-bone transition-colors hover:bg-brand-700"
      >
        Novo usuário
      </Link>
    </div>
  );

  if (consulta.isPending) {
    return (
      <div className="flex flex-col gap-6">
        {cabecalho}
        <p role="status" aria-live="polite" className="text-sm text-ink-soft">
          Carregando usuários…
        </p>
      </div>
    );
  }

  if (consulta.isError) {
    return (
      <div className="flex flex-col gap-6">
        {cabecalho}
        <div role="alert" className="border border-brass bg-bone-soft px-4 py-3 text-sm">
          <p>{mensagemDeErro(consulta.error)}</p>
          <button
            type="button"
            onClick={() => void consulta.refetch()}
            className="mt-2 text-brand-700 underline"
          >
            Tentar de novo
          </button>
        </div>
      </div>
    );
  }

  const { items, total } = consulta.data;
  const ultimaPagina = Math.max(1, Math.ceil(total / POR_PAGINA));

  return (
    <div className="flex flex-col gap-6">
      {cabecalho}

      {erroDeAcao && (
        <p role="alert" className="border border-brass bg-bone-soft px-4 py-3 text-sm">
          {erroDeAcao}
        </p>
      )}

      {items.length === 0 ? (
        <div className="border border-clay px-4 py-8 text-center">
          <p className="text-sm text-ink-soft">Nenhum usuário cadastrado ainda.</p>
          <Link to="/usuarios/novo" className="mt-2 inline-block text-sm text-brand-700 underline">
            Criar o primeiro
          </Link>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="border-b border-clay text-left text-xs tracking-[0.12em] text-ink-soft uppercase">
                <th className="py-2 pr-4 font-normal">Nome</th>
                <th className="py-2 pr-4 font-normal">E-mail</th>
                <th className="py-2 pr-4 font-normal">Papel</th>
                <th className="py-2 pr-4 font-normal">Situação</th>
                <th className="py-2 pr-4 font-normal">Último acesso</th>
                <th className="py-2 font-normal">
                  <span className="sr-only">Ações</span>
                </th>
              </tr>
            </thead>

            <tbody>
              {items.map((item) => {
                // A API recusa estas duas ações sobre a própria conta; a tela
                // não as oferece para a pessoa não descobrir por um erro.
                const ehVoce = item.id === usuario?.id;

                return (
                  <tr key={item.id} className="border-b border-sand">
                    <td className="py-3 pr-4">
                      {item.name}
                      {ehVoce && <span className="ml-2 text-xs text-ink-soft">(você)</span>}
                    </td>
                    <td className="py-3 pr-4 text-ink-soft">{item.email}</td>
                    <td className="py-3 pr-4">{PAPEL[item.role]}</td>
                    <td className="py-3 pr-4">
                      <Etiqueta ativo={item.isActive} />
                    </td>
                    <td className="py-3 pr-4 text-ink-soft">
                      {item.lastLoginAt
                        ? new Date(item.lastLoginAt).toLocaleString('pt-BR')
                        : 'nunca entrou'}
                    </td>
                    <td className="py-3 text-right whitespace-nowrap">
                      <Link to={`/usuarios/${item.id}`} className="text-brand-700 underline">
                        Editar
                      </Link>

                      {!ehVoce && (
                        <button
                          type="button"
                          onClick={() => void alternarAtivo(item)}
                          disabled={ocupado}
                          className="ml-4 text-brand-700 underline disabled:opacity-50"
                        >
                          {item.isActive ? 'Desativar' : 'Reativar'}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {ultimaPagina > 1 && (
        <nav aria-label="Paginação" className="flex items-center gap-4 text-sm">
          <button
            type="button"
            onClick={() => setPagina((p) => Math.max(1, p - 1))}
            disabled={pagina === 1}
            className="text-brand-700 underline disabled:opacity-40"
          >
            Anterior
          </button>

          <span className="text-ink-soft">
            Página {pagina} de {ultimaPagina} · {total} usuários
          </span>

          <button
            type="button"
            onClick={() => setPagina((p) => Math.min(ultimaPagina, p + 1))}
            disabled={pagina === ultimaPagina}
            className="text-brand-700 underline disabled:opacity-40"
          >
            Próxima
          </button>
        </nav>
      )}
    </div>
  );
}
